"use client";
import { updateState } from "@/commonJS/Actions/actionInterface";
// import { updateState } from "@/commonJS/Actions/actionInterface";
import getData from "@/commonJS/getData";
import { PayloadAction } from "@reduxjs/toolkit";
import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch,
} from "react";

type AppState = {
  layoutStore: any;
  pageStore: any;
};

const initialState: AppState = {
  layoutStore: {
    isAuthenticated: false,
    showLoginModal: false,
    loginModalStep: "1",
    profile: {},
    loader: {
      is_common_loader: false,
    },
    counter: {},
  },
  pageStore: {},
};

type LayoutAction =
  | { type: "SET_REDUX_STATE"; payload: any }
  | { type: "SET_LAYOUT_STATE"; payload: any }
  | { type: "SET_COUNT_STATE"; payload: any };
const updateStateFunc = (
  state: any,
  hierarchy: updateState,
  payload: any,
  actionState: any
) => {
  const { key, field, index } = hierarchy;
  let extractedIndex = index;
  if (typeof index === "object") {
    extractedIndex = getData(index, actionState) ?? 0;
  }
  if (!field) {
    if (extractedIndex !== undefined) {
      if (!Array.isArray(state[key])) {
        state[key] = [];
      }
      state[key][extractedIndex] = payload;
    } else {
      state[key] = payload;
    }
    return state;
  }
  if (!state[key]) {
    state[key] = extractedIndex !== undefined ? [] : {};
  }
  if (extractedIndex !== undefined) {
    if (!Array.isArray(state[key])) {
      state[key] = [];
    }
    if (!state[key][extractedIndex]) {
      state[key][extractedIndex] = {};
    }
    state[key][extractedIndex] = updateStateFunc(
      state[key][extractedIndex],
      field,
      payload,
      actionState
    );
  } else {
    state[key] = updateStateFunc(state[key], field, payload, actionState);
  }
  return state;
};

// Reducer
function appReducer(state: AppState, action: LayoutAction): AppState {
  switch (action.type) {
    case "SET_REDUX_STATE":
      const reduxActionPayload = action.payload;
      const newState = JSON.parse(JSON.stringify(state?.layoutStore ?? {}));
      if (reduxActionPayload?.action?.update) {
        return {
          ...state,
          layoutStore: updateStateFunc(
            newState,
            reduxActionPayload.action.update,
            reduxActionPayload.payload,
            action?.payload?.state
          ),
        };
      }
      return state;
    case "SET_LAYOUT_STATE":
      return {
        ...state,
        layoutStore: { ...state?.layoutStore, ...action.payload },
      };
    case "SET_COUNT_STATE":
      const counter = {
        [action.payload.key]: action.payload.secondsLeft,
      };
      return {
        ...state,
        layoutStore: { ...state?.layoutStore, counter: counter },
      };
    default:
      return state;
  }
}

type Thunk = (
  dispatch: Dispatch<LayoutAction | Thunk>,
  getState: () => AppState
) => void;

const AppStateContext = createContext<
  | {
      state: AppState;
      dispatch: Dispatch<LayoutAction | Thunk>;
    }
  | undefined
>(undefined);

const AppStateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatchBase] = useReducer(appReducer, initialState);

  const dispatch: Dispatch<LayoutAction | Thunk> = (action) => {
    if (typeof action === "function") {
      action(dispatch, () => state);
    } else {
      dispatchBase(action);
    }
  };

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
};

export default AppStateProvider;

// Hook
export const useAppState = () => {
  const context = useContext(AppStateContext);

  if (!context)
    throw new Error("useAppState must be used within AppStateProvider");
  return context;
};
